import React, { useEffect, useMemo, useState, useRef, lazy, Component } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ShoppingCart, Phone, Mail, Check, ChevronLeft, ChevronRight, MessageSquare, Info, ChevronDown, FileText, Download, Truck, Calculator as CalcIcon, X, Plus, Minus } from 'lucide-react';
import { categories } from '../data/products';
import { useCart } from '../components/CartContext';
import { QuoteModal } from '../components/QuoteModal';
import { SameDayBadge } from '../components/SameDayBadge';
import { generateSpecSheet } from '../utils/generateSpecSheet';
import { buildDeliveryLineItems, getDefaultQuantity, getSmallLoadSurcharge } from '../utils/loadSplit';
import { CartHelper } from '../components/CartHelper';
import { SimpleAreaCalculator } from '../components/SimpleAreaCalculator';
function CustomSelect({
  value,
  onChange,
  options,
  placeholder,
  className = ''
}: {
  value: string;
  onChange: (val: string) => void;
  options: {
    value: string;
    label: string;
    disabled?: boolean;
  }[];
  placeholder: string;
  className?: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const selectedOption = options.find(opt => opt.value === value);
  return <div className={`relative ${className}`} ref={dropdownRef} data-id="element-2849">
      <button type="button" onClick={() => setIsOpen(!isOpen)} className={`w-full px-4 py-3 bg-[#e8ede4] dark:bg-emerald-500/[0.07] border border-[#d4ddcf] dark:border-emerald-500/20 rounded-none text-brand-charcoal dark:text-white focus:outline-none focus:ring-2 focus:ring-[#5fb478] focus:border-transparent transition-all flex items-center justify-between font-medium hover:bg-[#dde5d6] dark:hover:bg-emerald-500/[0.12] ${value ? '' : 'text-stone-500'}`} data-id="element-2850">
        <span className="truncate" data-id="element-2851">
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <ChevronDown className={`w-4 h-4 ml-2 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} data-id="element-2852" />
      </button>

      <AnimatePresence data-id="element-2853">
        {isOpen && <motion.div initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -10
      }} transition={{
        duration: 0.15
      }} className="absolute z-50 w-full top-full mt-2 bg-white dark:bg-stone-900 border border-[#d4ddcf] dark:border-white/10 rounded-none shadow-xl overflow-hidden max-h-60 overflow-y-auto" data-id="element-2854">
            {options.map(opt => <button key={opt.value} type="button" disabled={opt.disabled} onClick={() => {
          if (!opt.disabled) {
            onChange(opt.value);
            setIsOpen(false);
          }
        }} className={`w-full text-left px-4 py-3 text-sm transition-colors ${opt.disabled ? 'text-stone-300 dark:text-white/30 cursor-not-allowed bg-stone-50 dark:bg-stone-900/50' : value === opt.value ? 'bg-[#e3f7e8] dark:bg-[#0f3e1e] text-[#1a7a3a] dark:text-[#a8e8b8] font-semibold' : 'text-brand-charcoal dark:text-white hover:bg-stone-50 dark:hover:bg-white/10'}`} data-id="element-2855">
                {opt.label}
              </button>)}
          </motion.div>}
      </AnimatePresence>
    </div>;
}
const ProductImageCarousel = ({
  images,
  alt
}: {
  images: string[];
  alt: string;
}) => {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    setIdx(0);
  }, [images]);
  if (!images || images.length <= 1) {
    return <img src={images[0]} alt={alt} className="absolute inset-0 w-full h-full object-cover" data-id="element-2856" />;
  }
  return <div className="absolute inset-0 w-full h-full group" data-id="element-2857">
      <img src={images[idx]} alt={alt} className="absolute inset-0 w-full h-full object-cover transition-opacity duration-300" data-id="element-2858" />
      <div className="absolute inset-0 flex items-center justify-between p-4 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-2859">
        <button onClick={e => {
        e.stopPropagation();
        setIdx(i => i === 0 ? images.length - 1 : i - 1);
      }} className="bg-black/50 text-white p-2 rounded-full hover:bg-black/70 transition-colors" data-id="element-2860">
          <ChevronLeft className="w-5 h-5" data-id="element-2861" />
        </button>
        <button onClick={e => {
        e.stopPropagation();
        setIdx(i => i === images.length - 1 ? 0 : i + 1);
      }} className="bg-black/50 text-white p-2 rounded-full hover:bg-black/70 transition-colors" data-id="element-2862">
          <ChevronRight className="w-5 h-5" data-id="element-2863" />
        </button>
      </div>
      <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-2" data-id="element-2864">
        {images.map((_, i) => <button key={i} onClick={e => {
        e.stopPropagation();
        setIdx(i);
      }} className={`w-2 h-2 rounded-full transition-colors ${i === idx ? 'bg-white' : 'bg-white/50 hover:bg-white/75'}`} data-id="element-2865" />)}
      </div>
    </div>;
};
export function Product() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {
    addToCart,
    openCart
  } = useCart();
  // Lazy-init the default quantity per product. Soil/Mulch/Org. products
  // (topsoil, bark, compost, play chips, fill dirt) default to a 15-yd
  // light load — that's the typical homeowner spring-project size and
  // the size of one full light-material truckload. The 50/50 Mix is
  // the explicit exception (defaults to 12 — see getDefaultQuantity in
  // utils/loadSplit.ts). Everything else (rock, drain, quarry, decor)
  // defaults to a 12-yd heavy load.
  const [quantity, setQuantity] = useState<string>(() => {
    for (const cat of categories) {
      const found = cat.products.find(p => p.slug === slug);
      if (found) return getDefaultQuantity(found.name);
    }
    return '12';
  });
  const [numLoads, setNumLoads] = useState<string>('');
  const [addedFlash, setAddedFlash] = useState(false);
  // Click-to-expand explainer for the Small-load rate label that now
  // sits ABOVE the big price number when the order is under the
  // small-load threshold. Replaces the verbose "surcharge applies"
  // subtext with a one-tap pulldown of plain-language pricing context.
  const [showSmallLoadInfo, setShowSmallLoadInfo] = useState(false);
  // Auto-dismiss the "Added to Truck!" confirmation card after 10s and
  // revert to the default Add-to-Truck state. Customer can still tap
  // Continue Shopping / View Cart at any point during the window.
  useEffect(() => {
    if (!addedFlash) return;
    const t = setTimeout(() => setAddedFlash(false), 10000);
    return () => clearTimeout(t);
  }, [addedFlash]);
  const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);
  // True when + has been pressed past 4 loads. Replaces the auto-fire
  // QuoteModal — the customer sees an amber "5+ loads" link below the
  // stepper and explicitly taps it to open the form.
  const [showQuoteWarning, setShowQuoteWarning] = useState(false);
  // Free-text yardage input — always active (no tap-to-edit handshake).
  // The readout *is* a transparent input styled to match the bold display.
  // Max 2 digits, clamped 1–96 on blur/Enter. The +/− pads keep this in
  // sync by writing to qtyDraft alongside quantity via the useEffect below.
  const [qtyDraft, setQtyDraft] = useState('12');
  const qtyInputRef = useRef<HTMLInputElement>(null);
  // Sync the input's displayed text whenever quantity changes externally
  // (e.g. via +/− pads, or estimatorQty handoff). Skip the sync if the
  // user is actively typing — we don't want to clobber their keystrokes.
  useEffect(() => {
    if (document.activeElement !== qtyInputRef.current) {
      setQtyDraft(String(Number(quantity) || 12));
    }
  }, [quantity]);
  const [activeTab, setActiveTab] = useState<'overview' | 'specs' | 'docs'>('overview');
  // Custom yardage handed off from the Instant Estimator via ?qty=N (e.g. 17).
  // When set, the page bypasses the standard dropdown and adds-to-cart using
  // the exact estimator yardage via buildDeliveryLineItems, so the 5yd-per-
  // load minimum and the delivery split match the estimator's quote exactly.
  // Clearing it (via the "Change quantity" link) reverts to the dropdown.
  const [estimatorQty, setEstimatorQty] = useState<number | null>(null);
  // Calculator Drawer State
  const [isCalcOpen, setIsCalcOpen] = useState(false);
  // Build flat list of all products for prev/next navigation
  const allProducts = useMemo(() => {
    const list: {
      slug: string;
      name: string;
      categoryName: string;
    }[] = [];
    for (const cat of categories) {
      for (const prod of cat.products) {
        list.push({
          slug: prod.slug,
          name: prod.name,
          categoryName: cat.name
        });
      }
    }
    return list;
  }, []);
  // Find product and its category
  let product = null;
  let category = null;
  for (const cat of categories) {
    const found = cat.products.find(p => p.slug === slug);
    if (found) {
      product = found;
      category = cat;
      break;
    }
  }
  const currentIndex = allProducts.findIndex(p => p.slug === slug);
  const prevProduct = currentIndex > 0 ? allProducts[currentIndex - 1] : null;
  const nextProduct = currentIndex < allProducts.length - 1 ? allProducts[currentIndex + 1] : null;
  const handleUseQuantity = (yards: number) => {
    const roundedYards = Math.ceil(yards);
    if (roundedYards === 0) return;
    const pn = product?.name.toLowerCase() || '';
    // 50/50 Mix is HEAVY (12 yd load) — excluded from the light list.
    const light = pn.includes('bark') || pn.includes('compost') || pn.includes('topsoil') || pn.includes('play chips');
    const fullLoad = light ? 15 : 12;
    if (roundedYards <= fullLoad) {
      // Single-load: just pre-select the dropdown value. The 5yd minimum is
      // enforced by the cart's billing math, so we display the actual yards
      // the user asked for (e.g. 6yd shows as 6, not 5).
      const minSingle = light ? 7 : 6;
      const snapped = Math.max(minSingle, roundedYards);
      setQuantity(String(snapped));
      setNumLoads('');
      setIsCalcOpen(false);
    } else if (product && category) {
      // Multi-load: add directly to cart using the shared splitter so the
      // last partial load is billed at the 5yd minimum (e.g. 16yd → 12 + 5).
      // This avoids the old behavior of inflating qty to fullLoad × loads.
      const lineItems = buildDeliveryLineItems(roundedYards, product.name);
      lineItems.forEach(li => {
        addToCart({
          category: category.name,
          product: li.product,
          quantity: li.quantity,
          pricePerYard: product.pricePerYard,
          requestedQuantity: li.requestedQuantity
        });
      });
      // Confirmation persists until the customer chooses Continue
      // Shopping or View Cart — gives them time to decide their next
      // step instead of the old 2s auto-dismiss.
      setAddedFlash(true);
      setIsCalcOpen(false);
    }
  };
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);
  // Pre-select quantity from estimator handoff (?qty=N). Runs after product
  // is resolved so we know whether it's light (15yd full load) or heavy (12yd).
  // - Single-load amounts (≤ fullLoad) snap to the dropdown directly.
  // - Multi-load amounts (e.g. 17yd) get stored as estimatorQty and the page
  //   adds-to-cart with the exact yardage on click, preserving the estimator's
  //   5yd-min split (12 + 5) rather than inflating to "2 full loads" (24).
  useEffect(() => {
    const qtyParam = searchParams.get('qty');
    if (!qtyParam) return;
    const n = parseInt(qtyParam, 10);
    if (isNaN(n) || n < 5) return;
    const pn = product?.name.toLowerCase() || '';
    // 50/50 Mix is HEAVY (12 yd load) — excluded from the light list.
    const light = pn.includes('bark') || pn.includes('compost') || pn.includes('topsoil') || pn.includes('play chips');
    const fullLoad = light ? 15 : 12;
    if (n <= fullLoad) {
      const minSingle = light ? 7 : 6;
      const snapped = Math.max(minSingle, Math.min(fullLoad, n));
      setQuantity(String(snapped));
      setNumLoads('');
      setEstimatorQty(null);
    } else {
      // Keep the exact estimator yardage. Dropdown stays at its default;
      // the Add to Truck button will use estimatorQty via buildDeliveryLineItems.
      setEstimatorQty(n);
    }
  }, [slug, searchParams, product?.name]);
  if (!product || !category) {
    return <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-4" data-id="element-2866">
        <h1 className="text-2xl font-bold text-brand-charcoal dark:text-white mb-4" data-id="element-2867">
          Product not found
        </h1>
        <button onClick={() => navigate('/')} className="text-brand-orange hover:underline" data-id="element-2868">
          Return to Home
        </button>
      </div>;
  }
  const isSpecialty = category.name === 'Specialty Rock';
  const pn = product.name.toLowerCase();
  // 50/50 Mix is HEAVY (12 yd load) — excluded from the light list.
  const isLight = pn.includes('bark') || pn.includes('compost') || pn.includes('topsoil') || pn.includes('play chips');
  const isLimitedTo12 = product.name === 'Builders Sand';
  const handleAddToCart = () => {
    if (quantity === 'multi' && numLoads === '5+') {
      setIsQuoteModalOpen(true);
      return;
    }
    // Estimator handoff takes priority — use the exact custom yardage so the
    // cart line items match the estimator's quote (e.g. 14 → 12 + 5, not 24).
    let finalQuantity: number;
    if (estimatorQty && estimatorQty > 0) {
      finalQuantity = estimatorQty;
    } else if (quantity === 'multi') {
      const deliveries = Number(numLoads);
      const fullLoadYards = isLight ? 15 : 12;
      finalQuantity = fullLoadYards * deliveries;
    } else {
      finalQuantity = Number(quantity);
    }
    // Split into per-delivery line items so the cart shows a clean breakdown
    // (Delivery 1 / Delivery 2 / ...) and the 5yd-per-load minimum is
    // enforced on any partial last load.
    const lineItems = buildDeliveryLineItems(finalQuantity, product.name);
    lineItems.forEach(li => {
      addToCart({
        category: category.name,
        product: li.product,
        quantity: li.quantity,
        pricePerYard: product.pricePerYard,
        requestedQuantity: li.requestedQuantity
      });
    });
    // Confirmation persists until the customer chooses Continue
    // Shopping or View Cart.
    setAddedFlash(true);
  };
  // Multi-load custom-quote redirect REMOVED. The amber 5+ warning
  // inside the readout window is now the SOLE entry point to the
  // QuoteModal — no auto-fire when numLoads flips to '5+'. The user
  // explicitly has to tap the warning to open the contact form,
  // matching the Hero estimator's behavior.
  // Reset the quantity dropdown to the per-product default whenever the
  // user navigates to a different product. React Router reuses the same
  // <Product /> component instance when only the :slug param changes, so
  // the useState lazy initializer above only runs on FIRST mount. Without
  // this effect, picking Medium Bark after viewing 1.25" Minus would
  // leave the dropdown stuck at 12 yd instead of snapping to the 15 yd
  // light-load default.
  useEffect(() => {
    if (product?.name) {
      setQuantity(getDefaultQuantity(product.name));
      setNumLoads('');
    }
    // Intentionally only re-runs on slug change — manual dropdown
    // adjustments by the customer within the same product are preserved.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);
  return <motion.main initial={{
    opacity: 0,
    y: 12
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.35,
    ease: 'easeOut'
  }} className="py-12 md:py-20 bg-[#fdfbf7] dark:bg-[#1b1b1b] min-h-[calc(100vh-64px)] transition-colors duration-300" data-id="element-2869">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2870">
        {/* Top nav: Back + Prev/Next */}
        <div className="flex items-center justify-between mb-8" data-id="element-2871">
          <Link to="/#materials" className="inline-flex items-center text-sm font-medium text-brand-muted hover:text-brand-orange transition-colors" data-id="element-2872">
            <ArrowLeft className="w-4 h-4 mr-2" data-id="element-2873" />
            Back to Materials
          </Link>
          <div className="flex items-center gap-2" data-id="element-2874">
            {prevProduct ? <Link to={`/shop/${prevProduct.slug}`} className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-brand-muted dark:text-stone-400 hover:text-brand-orange dark:hover:text-brand-orange bg-white dark:bg-[#333333] border border-stone-200 dark:border-[#444444] rounded-lg transition-colors" title={prevProduct.name} data-id="element-2875">
                <ChevronLeft className="w-4 h-4" data-id="element-2876" />
                <span className="hidden sm:inline max-w-[120px] truncate" data-id="element-2877">
                  {prevProduct.name}
                </span>
                <span className="sm:hidden" data-id="element-2878">Prev</span>
              </Link> : <span className="px-3 py-1.5 text-sm text-stone-300 dark:text-stone-600 cursor-not-allowed" data-id="element-2879">
                <ChevronLeft className="w-4 h-4" data-id="element-2880" />
              </span>}
            {nextProduct ? <Link to={`/shop/${nextProduct.slug}`} className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-brand-muted dark:text-stone-400 hover:text-brand-orange dark:hover:text-brand-orange bg-white dark:bg-[#333333] border border-stone-200 dark:border-[#444444] rounded-lg transition-colors" title={nextProduct.name} data-id="element-2881">
                <span className="hidden sm:inline max-w-[120px] truncate" data-id="element-2882">
                  {nextProduct.name}
                </span>
                <span className="sm:hidden" data-id="element-2883">Next</span>
                <ChevronRight className="w-4 h-4" data-id="element-2884" />
              </Link> : <span className="px-3 py-1.5 text-sm text-stone-300 dark:text-stone-600 cursor-not-allowed" data-id="element-2885">
                <ChevronRight className="w-4 h-4" data-id="element-2886" />
              </span>}
          </div>
        </div>

        <div className="bg-[#f4f7f2] dark:bg-[#333333] rounded-2xl border border-[#e6ece3] dark:border-[#444444] overflow-hidden shadow-sm flex flex-col md:flex-row" data-id="element-2887">
          {/* Image — 1:1 split with the content panel for a balanced
           hero feel where the product photo gets equal visual weight. */}
          <div className="w-full md:w-1/2 h-64 md:h-auto relative" data-id="element-2888">
            <ProductImageCarousel images={product.images || [product.image]} alt={product.name} data-id="element-2889" />
          </div>

          {/* Content */}
          <div className="w-full md:w-1/2 p-6 sm:p-8 md:p-10 flex flex-col" data-id="element-2890">
            <div className="mb-2" data-id="element-2891">
              <span className="text-xs font-bold text-brand-muted uppercase tracking-wider" data-id="element-2892">
                {category.name}
              </span>
            </div>
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-brand-charcoal dark:text-white mb-6" data-id="element-2893">
              {product.name}
            </h1>

            {/* Tabs */}
            <div className="flex border-b border-stone-200 dark:border-[#444] mb-6" data-id="element-2894">
              <button onClick={() => setActiveTab('overview')} className={`pb-3 px-4 text-sm font-bold transition-colors relative ${activeTab === 'overview' ? 'text-brand-orange' : 'text-stone-500 dark:text-stone-400 hover:text-stone-800 dark:hover:text-stone-200'}`} data-id="element-2895">
                Overview
                {activeTab === 'overview' && <motion.div layoutId="prodTab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-brand-orange" data-id="element-2896" />}
              </button>
              <button onClick={() => setActiveTab('specs')} className={`pb-3 px-4 text-sm font-bold transition-colors relative ${activeTab === 'specs' ? 'text-brand-orange' : 'text-stone-500 dark:text-stone-400 hover:text-stone-800 dark:hover:text-stone-200'}`} data-id="element-2897">
                Specs
                {activeTab === 'specs' && <motion.div layoutId="prodTab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-brand-orange" data-id="element-2898" />}
              </button>
              <button onClick={() => setActiveTab('docs')} className={`pb-3 px-4 text-sm font-bold transition-colors relative ${activeTab === 'docs' ? 'text-brand-orange' : 'text-stone-500 dark:text-stone-400 hover:text-stone-800 dark:hover:text-stone-200'}`} data-id="element-2899">
                Documents
                {activeTab === 'docs' && <motion.div layoutId="prodTab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-brand-orange" data-id="element-2900" />}
              </button>
            </div>

            <div className="mb-6" data-id="element-2901">
              <AnimatePresence mode="wait" data-id="element-2902">
                {activeTab === 'overview' && <motion.div key="overview" initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} transition={{
                duration: 0.2
              }} data-id="element-2903">
                    <p className="text-sm text-brand-charcoal/80 dark:text-white/80 mb-6 leading-relaxed" data-id="element-2904">
                      {product.description}
                    </p>

                    {product.bestFor && product.bestFor.length > 0 && <div className="mb-6" data-id="element-2905">
                        <h3 className="text-xs font-bold text-brand-charcoal dark:text-white uppercase tracking-wider mb-3" data-id="element-2906">
                          Best For
                        </h3>
                        <div className="flex flex-wrap gap-2" data-id="element-2907">
                          {product.bestFor.map((tag, idx) => <span key={idx} className="inline-flex items-center px-3 py-1 rounded-full bg-brand-lightgray dark:bg-[#2a2a2a] text-xs font-medium text-brand-charcoal dark:text-stone-300 border border-stone-200 dark:border-[#444444]" data-id="element-2908">
                              {tag}
                            </span>)}
                        </div>
                      </div>}

                    {product.proTip && <div className="border border-[#d4ddcf] dark:border-emerald-500/20 rounded-sm p-4 flex items-start gap-3" data-id="element-2909">
                        <div className="bg-[#059F40]/15 p-1.5 rounded-full shrink-0 mt-0.5" data-id="element-2910">
                          <Check className="w-4 h-4 text-[#059F40] dark:text-[#22c95c]" data-id="element-2911" />
                        </div>
                        <div data-id="element-2912">
                          <h4 className="text-sm font-bold text-brand-charcoal dark:text-white mb-1" data-id="element-2913">
                            Pro Tip
                          </h4>
                          <p className="text-sm text-brand-charcoal/80 dark:text-stone-300 leading-relaxed" data-id="element-2914">
                            {product.proTip}
                          </p>
                        </div>
                      </div>}
                  </motion.div>}

                {activeTab === 'specs' && <motion.div key="specs" initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} transition={{
                duration: 0.2
              }} data-id="element-2915">
                    {product.specs ? <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-2916">
                        {Object.entries(product.specs).map(([key, value]) => {
                    const labels: Record<string, string> = {
                      size: 'Size / Gradation',
                      weightPerYard: 'Density (Weight/Yd)',
                      compaction: 'Compaction Rating',
                      coverage: 'Est. Coverage'
                    };
                    return <div key={key} className="bg-stone-50 dark:bg-[#222222] p-4 rounded-xl border border-stone-200 dark:border-[#333333]" data-id="element-2917">
                              <p className="text-[10px] font-bold text-stone-500 dark:text-stone-400 uppercase tracking-wider mb-1.5" data-id="element-2918">
                                {labels[key] || key}
                              </p>
                              <p className="text-sm font-medium text-stone-900 dark:text-stone-200" data-id="element-2919">
                                {value}
                              </p>
                            </div>;
                  })}
                      </div> : <div className="text-center py-8 text-stone-500" data-id="element-2920">
                        No detailed specifications available for this product.
                      </div>}
                  </motion.div>}

                {activeTab === 'docs' && <motion.div key="docs" initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} transition={{
                duration: 0.2
              }} className="space-y-3" data-id="element-2921">
                    {/* Auto-generated Spec Sheet — real, downloadable */}
                    <button onClick={() => generateSpecSheet({
                  product,
                  categoryName: category.name
                })} className="w-full flex items-center justify-between p-4 rounded-xl border border-brand-orange/30 bg-brand-orange/5 hover:bg-brand-orange/10 hover:border-brand-orange/50 transition-all group text-left" data-id="element-2922">
                      <div className="flex items-center gap-3 min-w-0" data-id="element-2923">
                        <div className="w-10 h-10 rounded-lg bg-brand-orange/15 flex items-center justify-center shrink-0" data-id="element-2924">
                          <FileText className="w-5 h-5 text-brand-orange" data-id="element-2925" />
                        </div>
                        <div className="min-w-0" data-id="element-2926">
                          <p className="text-sm font-bold text-stone-900 dark:text-white" data-id="element-2927">
                            Product Spec Sheet
                          </p>
                          <p className="text-xs text-stone-500 dark:text-stone-400 truncate" data-id="element-2928">
                            One-page summary · specs, coverage, pricing
                          </p>
                        </div>
                      </div>
                      <span className="inline-flex items-center gap-1.5 text-xs font-bold text-brand-orange shrink-0 ml-3" data-id="element-2929">
                        <Download className="w-4 h-4" data-id="element-2930" />
                        <span className="hidden sm:inline" data-id="element-2931">Download</span>
                      </span>
                    </button>

                    {/* Need more? Email us — honest CTA for additional docs */}
                    <div className="flex items-start gap-3 p-4 rounded-xl border border-stone-200 dark:border-[#444] bg-stone-50 dark:bg-[#2a2a2a]" data-id="element-2932">
                      <div className="w-10 h-10 rounded-lg bg-stone-100 dark:bg-[#333] flex items-center justify-center shrink-0" data-id="element-2933">
                        <Mail className="w-4 h-4 text-stone-500 dark:text-stone-400" data-id="element-2934" />
                      </div>
                      <div className="min-w-0 flex-1" data-id="element-2935">
                        <p className="text-sm font-bold text-stone-900 dark:text-white mb-0.5" data-id="element-2936">
                          Need certified docs?
                        </p>
                        <p className="text-xs text-stone-500 dark:text-stone-400 leading-relaxed" data-id="element-2937">
                          Gradation curves, MSDS, and compaction test data are
                          available on request.{' '}
                          <a href={`mailto:info@253gravel.com?subject=${encodeURIComponent(`Certified docs request: ${product.name}`)}`} className="text-brand-orange font-semibold hover:underline break-all" data-id="element-2938">
                            info@253gravel.com
                          </a>
                          .
                        </p>
                      </div>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>

            <div data-id="element-2939">
              {isSpecialty ? <div className="space-y-4" data-id="element-2940">
                  <div className="p-4 bg-brand-lightgray/50 dark:bg-[#222222] rounded-xl border border-stone-200 dark:border-[#444444]" data-id="element-2941">
                    <p className="text-sm text-brand-charcoal dark:text-stone-300" data-id="element-2942">
                      Pricing and availability for specialty rock varies. Please
                      contact us directly for a custom quote.
                    </p>
                  </div>
                  <a href="tel:206-619-3742" className="w-full bg-brand-charcoal dark:bg-white text-white dark:text-brand-charcoal hover:bg-brand-charcoal/90 dark:hover:bg-stone-200 py-4 rounded-xl font-bold text-lg transition-colors flex items-center justify-center" data-id="element-2943">
                    <Phone className="w-5 h-5 mr-2" data-id="element-2944" />
                    Call for Pricing
                  </a>
                </div> : <div className="space-y-4" data-id="element-2945">
                  {(() => {
                const fullLoad = isLight ? 15 : 12;
                const smallLoadThreshold = isLight ? 12 : 10;
                const MIN = 3;
                const requestedYards = estimatorQty ? estimatorQty : quantity === 'multi' && numLoads && numLoads !== '5+' ? Number(numLoads) * fullLoad : quantity !== 'multi' ? Number(quantity) : 0;
                const loads: number[] = [];
                if (requestedYards > 0) {
                  if (requestedYards <= fullLoad) {
                    loads.push(Math.max(MIN, requestedYards));
                  } else {
                    const count = Math.ceil(requestedYards / fullLoad);
                    const last = Math.max(MIN, requestedYards - (count - 1) * fullLoad);
                    for (let i = 0; i < count - 1; i++) loads.push(fullLoad);
                    loads.push(last);
                  }
                }
                const billableYards = loads.reduce((a, b) => a + b, 0);
                const bulkPct = billableYards >= fullLoad * 4 ? 0.1 : billableYards >= (isLight ? fullLoad : fullLoad * 2) ? 0.05 : 0;
                // Build per-delivery line totals so the multi-load
                // breakdown can mirror the Services hub: each
                // delivery shows its yardage and its post-discount
                // dollar amount on its own row. preDiscountTotal is
                // the strikethrough "Was $X" anchor for the bulk
                // discount headline.
                const deliveryLoads: {
                  yards: number;
                  total: number;
                }[] = [];
                let hasSurchargedLoad = false;
                for (const ld of loads) {
                  let sur = 1;
                  if (ld < smallLoadThreshold) {
                    sur = 1 + (smallLoadThreshold - ld) * 0.1;
                  }
                  if (sur > 1) hasSurchargedLoad = true;
                  const loadTotal = Math.ceil(product.pricePerYard * sur) * ld;
                  deliveryLoads.push({
                    yards: ld,
                    total: loadTotal
                  });
                }
                const preDiscountTotal = deliveryLoads.reduce((s, l) => s + l.total, 0);
                let totalCost = preDiscountTotal;
                if (bulkPct > 0) {
                  totalCost = Math.round(totalCost * (1 - bulkPct));
                  // Apply the same bulk discount to each per-delivery
                  // total so the rows sum to the discounted grand
                  // total (with at most a rounding-cent of drift).
                  deliveryLoads.forEach((l, i) => {
                    deliveryLoads[i] = {
                      ...l,
                      total: Math.round(l.total * (1 - bulkPct))
                    };
                  });
                }
                const effectiveAvg = billableYards > 0 ? Math.round(totalCost / billableYards) : product.pricePerYard;
                const showsEffective = billableYards > 0 && (hasSurchargedLoad || bulkPct > 0) && effectiveAvg !== product.pricePerYard;
                // Past 4 full loads the order rolls into custom-quote
                // territory. The bulk-discount formula no longer
                // reflects what dispatch will actually charge (hand
                // loads, mixed truck sizes, etc.), so we suppress
                // the savings copy past that threshold rather than
                // surface a stale number.
                const isCustomQuoteZone = deliveryLoads.length >= 5;
                return <div className="border-b border-stone-100 dark:border-[#444444] pb-4" data-id="element-2946">
                        <div className="flex items-start justify-between gap-4" data-id="element-2947">
                          {/* Left: price column. The label above the
                           big number is state-aware:
                           - Bulk discount tier reached: amber pill
                           "5% Bulk Discount" (uppercase, prominent).
                           - Under the small-load threshold: clickable
                           "Small-load rate ⓘ" — tapping toggles a
                           full-width pulldown explainer below the
                           two-column header.
                           - Otherwise: the default "Price" label. */}
                          <div className="min-w-0 flex-1" data-id="element-2948">
                            {/* State label — all three variants render
                             at the same H-6 height so the big "$XX
                             /yard" number stays on the same baseline
                             as the right-column total regardless of
                             which label is showing.
                             Priority order:
                             1. Bulk discount (green pill) — customer
                             paying LESS.
                             2. Small-load surcharge (amber pill) —
                             customer paying MORE. Triggers off
                             hasSurchargedLoad alone so the pill
                             appears even on small-load orders where
                             the rounded effective rate happens to
                             match the base rate.
                             3. Default "Price" label otherwise. */}
                            <div className="h-6 mb-1 flex items-center" data-id="element-2949">
                              {!isCustomQuoteZone && bulkPct > 0 ? <span className="inline-flex items-center text-[10px] font-bold leading-none text-white bg-[#059F40] px-2 py-1 rounded-md uppercase tracking-wide" data-id="element-2950">
                                  {Math.round(bulkPct * 100)}% Bulk Discount
                                </span> : !isCustomQuoteZone && hasSurchargedLoad ? <span className="inline-flex items-center text-[10px] font-bold leading-none text-white bg-[#EAB308] px-2 py-1 rounded-md uppercase tracking-wide" data-id="element-2951">
                                  Small-load rate
                                </span> : <p className="text-sm text-brand-muted dark:text-[#22c95c] font-medium" data-id="element-2952">
                                  Price
                                </p>}
                            </div>
                            {isCustomQuoteZone ? <p className="text-lg font-semibold text-stone-500 dark:text-stone-400 whitespace-nowrap" data-id="element-2953">
                                Custom quote
                              </p> : <p className="text-3xl font-heading font-bold text-brand-charcoal dark:text-white" data-id="element-2954">
                                $
                                {showsEffective ? effectiveAvg : product.pricePerYard}{' '}
                                {/* Secondary "/ yard" label uses the
                                 same neutral muted-grey as the right
                                 column's "{N} yds /" and "+ delivery"
                                 so the metadata reads uniformly
                                 regardless of small-load / bulk
                                 discount state. "avg" dropped per
                                 design — the headline rate IS the
                                 customer's per-yard cost. */}
                                <span className="text-base font-normal text-brand-muted dark:text-stone-400" data-id="element-2955">
                                  / yard
                                </span>
                              </p>}
                            {/* Subtext under the big number. For a small
                             load: single line with strikethrough base
                             rate + plain "base rate at N yd+" copy
                             (the clickable label above carries the
                             surcharge messaging now). For a bulk
                             discount: single line with strikethrough
                             base rate + "Save $X on N yards". */}
                            {showsEffective && hasSurchargedLoad && !bulkPct && <p className="text-xs text-brand-muted dark:text-stone-400 mt-1.5 whitespace-nowrap" data-id="element-2956">
                                  ${product.pricePerYard}/yd at{' '}
                                  {smallLoadThreshold} yd+
                                </p>}
                            {showsEffective && bulkPct > 0 && !isCustomQuoteZone && (
                      /* Savings copy under the price reads
                      accent green (matches the green pill
                      above) — the customer is paying LESS
                      here, so the color reinforces that
                      even when the eye skips the label. */
                      <p className="text-sm mt-1" data-id="element-2957">
                                  <span className="line-through text-stone-500 dark:text-stone-400" data-id="element-2958">
                                    ${product.pricePerYard}/yd
                                  </span>
                                  <span className="text-[#059F40] ml-2 font-semibold" data-id="element-2959">
                                    $
                                    {product.pricePerYard * billableYards - totalCost}{' '}
                                    savings
                                  </span>
                                </p>)}
                            {!showsEffective && billableYards === 0 && <p className="text-xs text-brand-muted dark:text-stone-400 mt-1.5" data-id="element-2960">
                                Base price at {smallLoadThreshold}+ yards per
                                delivery. Smaller loads include a surcharge.
                              </p>}
                          </div>
                          {/* Right: materials subtotal — mirrors the
                           "$87 / yard" pattern on the left by putting the
                           small qualifier ("12 yds /") IN FRONT of the
                           big number ("$1,044"). The leading-blank top
                           row keeps the big numbers baseline-aligned
                           with the Price column even though the right
                           column has no top label. */}
                          {billableYards > 0 && <div className="flex-1 min-w-0 text-right" data-id="element-2961">
                              {/* invisible spacer matching the
                               left-column label container so both
                               big numbers land on the same baseline
                               regardless of which state label
                               (Price / Small-load rate / Bulk
                               Discount) is showing. */}
                              <div aria-hidden="true" className="h-6 mb-1 select-none" data-id="element-2962" />
                              {isCustomQuoteZone ? <>
                                  <p className="text-lg font-semibold text-stone-500 dark:text-stone-400 whitespace-nowrap" data-id="element-2963">
                                    <span className="text-sm font-normal text-brand-muted dark:text-[#22c95c]" data-id="element-2964">
                                      {billableYards} yds /
                                    </span>{' '}
                                    Custom
                                  </p>
                                  <p className="text-xs text-brand-orange mt-0.5" data-id="element-2965">
                                    text us for pricing
                                  </p>
                                </> : <>
                                  {/* Secondary metadata ("{N} yds /"
                                   and "+ delivery") uses the same
                                   neutral muted-grey as the left
                                   column's "/ yard" so the surrounding
                                   metadata reads uniformly white →
                                   grey regardless of discount state.
                                   Only the BIG numbers stay white. */}
                                  <p className="text-3xl font-heading font-bold text-brand-charcoal dark:text-white whitespace-nowrap" data-id="element-2966">
                                    <span className="text-base font-normal text-brand-muted dark:text-stone-400" data-id="element-2967">
                                      {billableYards} yds /
                                    </span>{' '}
                                    ${totalCost.toLocaleString()}
                                  </p>
                                  {/* "+ delivery" mirrors the green
                                   "Price" label on the left so the
                                   two columns book-end the row in
                                   matching accent colors. */}
                                  <p className="text-xs text-brand-muted dark:text-[#22c95c] mt-0.5" data-id="element-2968">
                                    + delivery
                                  </p>
                                </>}
                            </div>}
                        </div>

                        {/* Section-wide explainer pulldown for the
                         Small-load rate label. Spans the entire price
                         card so it has room to breathe (rather than
                         squeezing into the left column under the
                         numbers). */}
                        <AnimatePresence data-id="element-2969">
                          {showSmallLoadInfo && !isCustomQuoteZone && showsEffective && hasSurchargedLoad && !bulkPct && <motion.div initial={{
                      opacity: 0,
                      height: 0
                    }} animate={{
                      opacity: 1,
                      height: 'auto'
                    }} exit={{
                      opacity: 0,
                      height: 0
                    }} transition={{
                      duration: 0.2
                    }} className="overflow-hidden" data-id="element-2970">
                                <p className="mt-3 p-3 rounded-lg bg-[#EAB308]/10 border border-[#EAB308]/30 text-xs text-stone-700 dark:text-white/85 leading-relaxed" data-id="element-2971">
                                  A small load runs the truck below capacity.
                                  Fixed loading costs (loader operator, scale,
                                  ticketing) get split across fewer yards, so
                                  the per-yard rate goes up. Order{' '}
                                  {smallLoadThreshold}+ yd in a single delivery
                                  to drop to the base ${product.pricePerYard}/yd
                                  rate.
                                </p>
                              </motion.div>}
                        </AnimatePresence>

                        {/* Materials breakdown — ALWAYS visible when
                         there's a quantity. Surfaces the per-yard rate
                         in plain language: single small load → 1
                         "Small-load rate" line; single full load or
                         all-full multi-load → 1 "Full-load rate" (or
                         bulk-discount) line; full + partial mix → 2
                         lines so the customer sees exactly which yards
                         run at each rate. */}
                        {billableYards > 0 && !isCustomQuoteZone && (() => {
                    const _fullLoadCount = Math.floor(billableYards / fullLoad);
                    const _fullLoadYards = _fullLoadCount * fullLoad;
                    const _partialYards = billableYards - _fullLoadYards;
                    const _partialSurcharge = _partialYards > 0 ? getSmallLoadSurcharge(_partialYards, product.name) : 1;
                    // Derive the Materials-breakdown numbers
                    // from the source-of-truth `totalCost`
                    // (which has the bulk discount applied at
                    // the whole-order level with proper
                    // rounding). The OLD approach rounded the
                    // per-yard rate first then multiplied,
                    // which drifted from totalCost by several
                    // dollars on bulk-discounted orders
                    // (e.g. 24 yd @ 5% showed $73/yd × 24 =
                    // $1,752 while the actual total was
                    // $1,733). Now the breakdown ALWAYS
                    // reconciles to totalCost exactly.
                    //
                    // Strategy:
                    //   1. Compute the full-load subtotal as
                    //      the pro-rated share of totalCost
                    //      that the full-load yards represent
                    //      (using the un-surcharged base
                    //      rate × discounted price). For pure
                    //      full-load orders this equals
                    //      totalCost.
                    //   2. Compute partial subtotal as
                    //      totalCost − fullLoadSubtotal so
                    //      the two lines always sum to the
                    //      grand total.
                    //   3. Per-yard rates are derived from
                    //      the subtotals so they match what
                    //      the customer is actually paying.
                    const _partialBaseShare = _partialYards * _partialSurcharge;
                    const _fullLoadBaseShare = _fullLoadYards;
                    const _baseShareSum = _fullLoadBaseShare + _partialBaseShare;
                    const fullLoadSubtotal = _fullLoadYards > 0 && _baseShareSum > 0 ? Math.round(totalCost * _fullLoadBaseShare / _baseShareSum) : 0;
                    const partialSubtotal = _partialYards > 0 ? totalCost - fullLoadSubtotal : 0;
                    const fullLoadPerYd = _fullLoadYards > 0 ? Math.round(fullLoadSubtotal / _fullLoadYards) : Math.round(product.pricePerYard * (1 - bulkPct));
                    const partialPerYd = _partialYards > 0 ? Math.round(partialSubtotal / _partialYards) : 0;
                    const hasMixedBreakdown = _fullLoadCount > 0 && _partialYards > 0;
                    const fullLoadTagFull = bulkPct > 0 ? <span className="text-[#059F40] font-semibold" data-id="element-2972">
                                  {Math.round(bulkPct * 100)}% bulk discount
                                </span> : <span className="text-brand-charcoal/50 dark:text-white/50 font-medium" data-id="element-2973">
                                  Full-load rate
                                </span>;
                    return <div className="mt-4 pt-3 border-t border-brand-charcoal/5 dark:border-white/5 space-y-1.5" data-id="element-2974">
                                <div className="text-xs uppercase tracking-wider font-bold text-brand-charcoal/60 dark:text-white/60 mb-1" data-id="element-2975">
                                  Materials
                                </div>
                                {hasMixedBreakdown ? <>
                                    {/* Full-load line */}
                                    <div className="flex items-baseline justify-between gap-3" data-id="element-2976">
                                      <div className="min-w-0 text-xs text-brand-charcoal/70 dark:text-white/70 flex items-center flex-wrap gap-x-2" data-id="element-2977">
                                        <span data-id="element-2978">
                                          ${fullLoadPerYd}/yd × {_fullLoadYards}{' '}
                                          yd
                                        </span>
                                        {fullLoadTagFull}
                                      </div>
                                      <div className="text-sm font-bold text-brand-charcoal dark:text-white tabular-nums shrink-0" data-id="element-2979">
                                        ${fullLoadSubtotal.toLocaleString()}
                                      </div>
                                    </div>
                                    {/* Partial-load line. The
                                     "Small-load rate" tag is the
                                     clickable trigger for the
                                     section-wide explainer pulldown
                                     (Info icon + dotted underline). */}
                                    <div className="flex items-baseline justify-between gap-3" data-id="element-2980">
                                      <div className="min-w-0 text-xs text-brand-charcoal/70 dark:text-white/70 flex items-center flex-wrap gap-x-2" data-id="element-2981">
                                        <span data-id="element-2982">
                                          ${partialPerYd}/yd × {_partialYards}{' '}
                                          yd
                                        </span>
                                        <button type="button" onClick={() => setShowSmallLoadInfo(v => !v)} aria-expanded={showSmallLoadInfo} className="inline-flex items-center gap-1 text-[#EAB308] font-semibold hover:opacity-90 transition-opacity" data-id="element-2983">
                                          <span className="underline underline-offset-2 decoration-dotted" data-id="element-2984">
                                            Small-load rate
                                          </span>
                                          <Info className="w-3 h-3" data-id="element-2985" />
                                        </button>
                                      </div>
                                      <div className="text-sm font-bold text-brand-charcoal dark:text-white tabular-nums shrink-0" data-id="element-2986">
                                        ${partialSubtotal.toLocaleString()}
                                      </div>
                                    </div>
                                  </> : _partialYards > 0 && _partialSurcharge > 1 ? (
                      /* Single small-load order BELOW the
                      threshold — surcharge in effect,
                      tag the line "Small-load rate". */
                      <div className="flex items-baseline justify-between gap-3" data-id="element-2987">
                                    <div className="min-w-0 text-xs text-brand-charcoal/70 dark:text-white/70 flex items-center flex-wrap gap-x-2" data-id="element-2988">
                                      <span data-id="element-2989">
                                        ${partialPerYd}/yd × {_partialYards} yd
                                      </span>
                                      <button type="button" onClick={() => setShowSmallLoadInfo(v => !v)} aria-expanded={showSmallLoadInfo} className="inline-flex items-center gap-1 text-[#EAB308] font-semibold hover:opacity-90 transition-opacity" data-id="element-2990">
                                        <span className="underline underline-offset-2 decoration-dotted" data-id="element-2991">
                                          Small-load rate
                                        </span>
                                        <Info className="w-3 h-3" data-id="element-2992" />
                                      </button>
                                    </div>
                                    <div className="text-sm font-bold text-brand-charcoal dark:text-white tabular-nums shrink-0" data-id="element-2993">
                                      ${partialSubtotal.toLocaleString()}
                                    </div>
                                  </div>) : _partialYards > 0 ? (
                      /* At-or-above-threshold but below
                      one full load (e.g. 10–11 yd on
                      heavy materials) — surcharge = 1,
                      so it's effectively the full-load
                      rate. Render with the full-load
                      tag (or bulk-discount tag). */
                      <div className="flex items-baseline justify-between gap-3" data-id="element-2994">
                                    <div className="min-w-0 text-xs text-brand-charcoal/70 dark:text-white/70 flex items-center flex-wrap gap-x-2" data-id="element-2995">
                                      <span data-id="element-2996">
                                        ${partialPerYd}/yd × {_partialYards} yd
                                      </span>
                                      {fullLoadTagFull}
                                    </div>
                                    <div className="text-sm font-bold text-brand-charcoal dark:text-white tabular-nums shrink-0" data-id="element-2997">
                                      ${partialSubtotal.toLocaleString()}
                                    </div>
                                  </div>) : (
                      /* Pure full-load order (1+ full
                      loads, no partial) — one line at
                      the full-load rate or bulk-discount
                      rate. */
                      <div className="flex items-baseline justify-between gap-3" data-id="element-2998">
                                    <div className="min-w-0 text-xs text-brand-charcoal/70 dark:text-white/70 flex items-center flex-wrap gap-x-2" data-id="element-2999">
                                      <span data-id="element-3000">
                                        ${fullLoadPerYd}/yd × {_fullLoadYards}{' '}
                                        yd
                                      </span>
                                      {fullLoadTagFull}
                                    </div>
                                    <div className="text-sm font-bold text-brand-charcoal dark:text-white tabular-nums shrink-0" data-id="element-3001">
                                      ${fullLoadSubtotal.toLocaleString()}
                                    </div>
                                  </div>)}
                                {deliveryLoads.length > 1 && <p className="text-xs text-brand-orange mt-1" data-id="element-3002">
                                    + additional delivery fee for{' '}
                                    {deliveryLoads.length - 1} extra{' '}
                                    {deliveryLoads.length > 2 ? 'loads' : 'load'}
                                  </p>}
                              </div>;
                  })()}
                      </div>;
              })()}

                  {/* Estimator handoff banner — appears when the user
                   arrived from the Instant Estimator with a custom yardage
                   that needs to split across multiple deliveries. Without
                   this chip, the dropdown still shows its default ("12 Yards")
                   and the user thinks the page ignored their calculated
                   amount. Clicking "Change quantity" reverts to the dropdown. */}
                  {estimatorQty && estimatorQty > 0 && (() => {
                const loads = (() => {
                  const fullLoad = isLight ? 15 : 12;
                  const MIN = 5;
                  const n = estimatorQty;
                  if (n <= fullLoad) return [Math.max(MIN, n)];
                  const count = Math.ceil(n / fullLoad);
                  const last = n - (count - 1) * fullLoad;
                  const billableLast = Math.max(MIN, last);
                  const arr: number[] = [];
                  for (let i = 0; i < count - 1; i++) arr.push(fullLoad);
                  arr.push(billableLast);
                  return arr;
                })();
                const breakdown = loads.join(' + ');
                const totalBillable = loads.reduce((a, b) => a + b, 0);
                return <motion.div initial={{
                  opacity: 0,
                  y: -6
                }} animate={{
                  opacity: 1,
                  y: 0
                }} className="rounded-xl border border-[#059F40]/30 bg-[#059F40]/10 p-3 sm:p-4" data-id="element-3003">
                          <div className="flex items-start gap-3" data-id="element-3004">
                            <div className="w-8 h-8 rounded-full bg-[#059F40]/20 flex items-center justify-center shrink-0" data-id="element-3005">
                              <Check className="w-4 h-4 text-[#059F40]" data-id="element-3006" />
                            </div>
                            <div className="flex-1 min-w-0" data-id="element-3007">
                              <p className="text-sm font-bold text-brand-charcoal dark:text-white" data-id="element-3008">
                                From your estimator: {estimatorQty}{' '}
                                {estimatorQty === 1 ? 'yard' : 'yards'}
                              </p>
                              <p className="text-xs text-brand-charcoal/70 dark:text-stone-300 mt-0.5" data-id="element-3009">
                                Splits into {loads.length}{' '}
                                {loads.length === 1 ? 'delivery' : 'deliveries'}{' '}
                                ({breakdown} yd)
                                {totalBillable !== estimatorQty && <span className="text-amber-700 dark:text-amber-400" data-id="element-3010">
                                    {' '}
                                    · billed as {totalBillable} yd (3 yd order
                                    min)
                                  </span>}
                              </p>
                            </div>
                            <button type="button" onClick={() => setEstimatorQty(null)} className="shrink-0 text-xs font-semibold text-brand-charcoal/70 dark:text-stone-400 hover:text-brand-orange dark:hover:text-brand-orange underline underline-offset-2" data-id="element-3011">
                              Change
                            </button>
                          </div>
                        </motion.div>;
              })()}

                  <div className="space-y-2" data-id="element-3012">
                    <div className="flex items-center justify-between gap-3 flex-wrap" data-id="element-3013">
                      <label className="block text-sm font-bold text-brand-charcoal dark:text-stone-300" data-id="element-3014">
                        {estimatorQty ? 'Or change quantity' : 'Select Quantity'}
                      </label>
                      {(() => {
                    const fullLoad = isLight ? 15 : 12;
                    const tier2Yards = isLight ? fullLoad : fullLoad * 2;
                    const tier3Yards = fullLoad * 4;
                    return <span className="text-[11px] text-brand-muted dark:text-stone-400" data-id="element-3015">
                            5% at {tier2Yards} yd · 10% at {tier3Yards} yd
                          </span>;
                  })()}
                    </div>
                    {/* Floating stepper — no container background, just
                     big bold + and − pads flanking the live readout.
                     active:* gives instant green flash on press. Below
                     a full load increments by 1 yard; at/above a full
                     load snaps to the next full-load multiple (2, 3,
                     4 loads). + at 4 loads flips the row below into an
                     amber "Need 5+ loads?" link (does NOT auto-open the
                     custom-quote modal — the customer explicitly taps
                     the link). */}
                    <div className="flex flex-col gap-3" data-id="element-3016">
                      <div className="flex items-center justify-between gap-3" data-id="element-3017">
                        <button type="button" onClick={() => {
                      const fullLoad = isLight ? 15 : 12;
                      const MIN = 3;
                      const current = Number(quantity) || 12;
                      if (current <= fullLoad) {
                        if (current > MIN) setQuantity(String(current - 1));
                      } else {
                        const currentLoads = Math.round(current / fullLoad);
                        const newLoads = currentLoads - 1;
                        setQuantity(String(newLoads * fullLoad));
                        // Clear the amber CTA once back at 4 loads or less.
                        if (newLoads <= 4) setShowQuoteWarning(false);
                      }
                    }} aria-label="Decrease quantity" className="w-16 h-16 sm:w-20 sm:h-20 shrink-0 flex items-center justify-center rounded-xl border border-stone-200 dark:border-white/15 text-brand-charcoal dark:text-white hover:bg-stone-100 dark:hover:bg-white/5 active:bg-[#059F40]/20 active:border-[#059F40] active:text-[#059F40] dark:active:bg-[#22c95c]/15 dark:active:border-[#22c95c] dark:active:text-[#22c95c] transition-colors duration-75" data-id="element-3018">
                          <Minus className="w-10 h-10" strokeWidth={2.5} data-id="element-3019" />
                        </button>
                        <div className="flex-1 text-center text-2xl font-bold text-brand-charcoal dark:text-white tabular-nums leading-tight" data-id="element-3020">
                          {(() => {
                        const current = Number(quantity) || 12;
                        const fullLoad = isLight ? 15 : 12;
                        const loadCount = current > fullLoad ? Math.round(current / fullLoad) : 1;
                        const isFullLoad = current === fullLoad;
                        const isMin = current === 3;
                        const commit = () => {
                          const parsed = parseInt(qtyDraft, 10);
                          if (isNaN(parsed) || parsed <= 0) {
                            // Empty / invalid → revert input text to current.
                            setQtyDraft(String(current));
                            return;
                          }
                          // Cap at 8 loads (96 yd heavy / 120 yd light).
                          const max = fullLoad * 8;
                          const clamped = Math.min(max, Math.max(1, parsed));
                          setQuantity(String(clamped));
                          setQtyDraft(String(clamped));
                          // Flip the amber "text us" CTA once past 4 loads.
                          if (clamped > fullLoad * 4) {
                            setShowQuoteWarning(true);
                          } else {
                            setShowQuoteWarning(false);
                          }
                        };
                        return <>
                                <input ref={qtyInputRef} type="text" inputMode="numeric" pattern="[0-9]*" value={qtyDraft} maxLength={2} onFocus={e => e.currentTarget.select()} onChange={e => {
                            // Numeric only, max 2 digits.
                            const v = e.target.value.replace(/[^0-9]/g, '').slice(0, 2);
                            setQtyDraft(v);
                          }} onBlur={commit} onKeyDown={e => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              (e.currentTarget as HTMLInputElement).blur();
                            } else if (e.key === 'Escape') {
                              e.preventDefault();
                              setQtyDraft(String(current));
                              (e.currentTarget as HTMLInputElement).blur();
                            }
                          }} aria-label="Quantity in yards" style={{
                            width: `${Math.max(1, qtyDraft.length)}ch`
                          }} className={`bg-transparent border-0 outline-none text-center text-2xl font-bold tabular-nums p-0 m-0 focus:ring-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none transition-colors ${isMin ? 'text-[#EAB308]' : 'text-brand-charcoal dark:text-white'}`} data-id="element-3021" />
                                <span className={`hidden sm:inline transition-colors ${isMin ? 'text-[#EAB308]' : 'text-brand-charcoal dark:text-white'}`} data-id="element-3022">
                                  {' '}
                                  yards
                                </span>
                                <span className={`sm:hidden transition-colors ${isMin ? 'text-[#EAB308]' : 'text-brand-charcoal dark:text-white'}`} data-id="element-3023">
                                  {' '}
                                  yd
                                </span>
                                <span className="ml-2 text-[11px] font-normal text-brand-muted dark:text-stone-400 align-baseline" data-id="element-3024">
                                  {loadCount}{' '}
                                  {loadCount === 1 ? 'load' : 'loads'}
                                </span>
                              </>;
                      })()}
                        </div>
                        <button type="button" onClick={() => {
                      const current = Number(quantity) || 12;
                      const fullLoad = isLight ? 15 : 12;
                      if (current < fullLoad) {
                        setQuantity(String(current + 1));
                      } else {
                        const currentLoads = Math.round(current / fullLoad);
                        // Cap at 8 loads. Past 4 loads the customer
                        // is in "large order" territory and the
                        // primary CTA flips to the amber "text us"
                        // button — but the stepper still works so
                        // they can show us the exact yardage they
                        // need before tapping through to SMS.
                        if (currentLoads >= 8) return;
                        const newLoads = currentLoads + 1;
                        setQuantity(String(newLoads * fullLoad));
                        if (newLoads >= 5) setShowQuoteWarning(true);
                      }
                    }} aria-label="Increase quantity" className="w-16 h-16 sm:w-20 sm:h-20 shrink-0 flex items-center justify-center rounded-xl border border-stone-200 dark:border-white/15 text-brand-charcoal dark:text-white hover:bg-stone-100 dark:hover:bg-white/5 active:bg-[#059F40]/20 active:border-[#059F40] active:text-[#059F40] dark:active:bg-[#22c95c]/15 dark:active:border-[#22c95c] dark:active:text-[#22c95c] transition-colors duration-75" data-id="element-3025">
                          <Plus className="w-10 h-10" strokeWidth={2.5} data-id="element-3026" />
                        </button>
                      </div>

                      {/* Large-order handoff — when + has pushed past
                       4 loads, the primary CTA below morphs into an
                       amber "Text us for a quote" button instead of a
                       separate amber pulldown. */}

                      {addedFlash ? <motion.div initial={{
                    opacity: 0,
                    y: 10
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} className="space-y-3" data-id="element-3027">
                          <div className="w-full flex items-center justify-center bg-[#059F40]/10 dark:bg-[#006241]/40 text-[#059F40] dark:text-[#22c95c] rounded-none font-bold border border-[#059F40] dark:border-[#22c95c] py-4 text-lg" data-id="element-3028">
                            <Check className="w-5 h-5 mr-2" data-id="element-3029" />
                            Added to Truck!
                          </div>
                          <div className="flex gap-2" data-id="element-3030">
                            <button onClick={() => {
                        setAddedFlash(false);
                        navigate('/#materials');
                      }} className="flex-1 py-2.5 rounded-xl text-sm font-bold text-brand-charcoal dark:text-white border border-stone-200 dark:border-white/10 bg-white/50 dark:bg-white/5 hover:bg-white/80 dark:hover:bg-white/10 transition-colors" data-id="element-3031">
                              Continue Shopping
                            </button>
                            <button onClick={() => {
                        setAddedFlash(false);
                        openCart();
                      }} className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white bg-brand-orange hover:bg-brand-orangeHover transition-colors flex items-center justify-center gap-1.5" data-id="element-3032">
                              <ShoppingCart className="w-4 h-4" data-id="element-3033" />
                              View Cart
                            </button>
                          </div>
                        </motion.div> : showQuoteWarning ? <button onClick={() => {
                    const isMobile = typeof navigator !== 'undefined' && /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
                    if (!isMobile) {
                      setIsQuoteModalOpen(true);
                      return;
                    }
                    const fullLoad = isLight ? 15 : 12;
                    const yds = Number(quantity) || fullLoad * 5;
                    const loads = Math.max(5, Math.round(yds / fullLoad));
                    const body = `Hi 253Gravel — I need a custom quote for ${loads} loads of ${product.name} (${yds} yd). Thanks!`;
                    window.location.href = `sms:+12066193742?&body=${encodeURIComponent(body)}`;
                  }} className="w-full py-[18px] rounded-none font-bold text-lg transition-all duration-300 flex items-center justify-center bg-amber-500/15 dark:bg-amber-500/15 hover:bg-amber-500/25 dark:hover:bg-amber-500/25 text-amber-700 dark:text-amber-300 border-2 border-amber-500/40 hover:border-amber-500/60" aria-live="polite" data-id="element-3034">
                          <MessageSquare className="w-5 h-5 mr-2" data-id="element-3035" />
                          Large order — text us →
                        </button> : (() => {
                    const current = Number(quantity) || 12;
                    const fullLoad = isLight ? 15 : 12;
                    const loadPct = Math.min(100, Math.max(0, current / fullLoad * 100));
                    return <button onClick={handleAddToCart} className="relative w-full overflow-hidden bg-[#04702E] text-white py-[18px] rounded-none font-bold text-lg flex items-center justify-center shadow-[0_0_12px_rgba(5,137,77,0.4)]" data-id="element-3036">
                              <motion.span aria-hidden="true" className="absolute inset-y-0 left-0 bg-[#059F40]" initial={false} animate={{
                        width: `${loadPct}%`
                      }} transition={{
                        type: 'spring',
                        stiffness: 260,
                        damping: 26
                      }} data-id="element-3037" />
                              <span className="relative z-10 flex items-center justify-center" data-id="element-3038">
                                <Truck className="w-5 h-5 mr-2" data-id="element-3039" />
                                Add to Truck
                              </span>
                            </button>;
                  })()}
                    </div>

                    <div className="flex justify-center" data-id="element-3040">
                      <SameDayBadge variant="full" data-id="element-3041" />
                    </div>

                    {/* Material sanity check — helps the customer confirm
                     the product they're viewing matches their project
                     BEFORE adding to cart. Lives here (not in the cart
                     drawer) so the decision happens with intent fresh.
                     The helper is now category-aware: each category in
                     data/products.ts ships its own pair of questions
                     and its own recommendation table inside
                     components/CartHelper.tsx, so the suggestions stay
                     relevant whether the customer is browsing base rock,
                     clear & drain rock, soil/mulch, quarry rip-rap, or
                     decorative landscape stone. */}
                    <CartHelper contextProduct={{
                  slug: product.slug,
                  name: product.name
                }} data-id="element-3042" />

                    {/* Inline Coverage Calculator — pulldown style, matches
                     the materials hub (Services) calculator. Replaces the
                     old fixed side drawer so the experience is identical
                     whether the customer arrives via the hub or a direct
                     /shop/:slug link. Reuses the existing calculator
                     state (shape/length/width/diameter/base/height/depth
                     /calcYards/calcTons) and `handleUseQuantity()`. */}
                    <div className="mt-3 border-t border-brand-charcoal/10 dark:border-white/10 pt-3" data-id="element-3043">
                      <button onClick={() => setIsCalcOpen(v => !v)} className="w-full flex items-center justify-center gap-2 py-2 text-sm font-medium text-brand-orange hover:text-brand-orangeHover transition-colors" aria-expanded={isCalcOpen} data-id="element-3044">
                        <CalcIcon className="w-4 h-4" data-id="element-3045" />
                        {isCalcOpen ? 'Hide Calculator' : 'Calculate how much I need'}
                      </button>
                      <AnimatePresence data-id="element-3046">
                        {isCalcOpen && <motion.div initial={{
                      opacity: 0,
                      height: 0
                    }} animate={{
                      opacity: 1,
                      height: 'auto'
                    }} exit={{
                      opacity: 0,
                      height: 0
                    }} transition={{
                      duration: 0.2
                    }} className="overflow-hidden" data-id="element-3047">
                            <div className="mt-4" data-id="element-3048">
                              <SimpleAreaCalculator materialSlug={product.slug} onApply={handleUseQuantity} data-id="element-3049" />
                            </div>
                          </motion.div>}
                      </AnimatePresence>
                    </div>

                    {/* Multi-load delivery-fee notice — visible whenever
                     the stepper is open. The "please select how many"
                     prompt is no longer needed because the stepper
                     auto-seeds 2 loads on entering multi mode. */}
                    <AnimatePresence data-id="element-3050">
                      {quantity === 'multi' && <motion.div initial={{
                    opacity: 0,
                    height: 0
                  }} animate={{
                    opacity: 1,
                    height: 'auto'
                  }} exit={{
                    opacity: 0,
                    height: 0
                  }} className="overflow-hidden" data-id="element-3051">
                          <div className="flex items-start gap-2 mt-3 p-3 bg-brand-orange/10 border border-brand-orange/20 rounded-lg" data-id="element-3052">
                            <Info className="w-4 h-4 text-brand-orange mt-0.5 shrink-0" data-id="element-3053" />
                            <p className="text-xs text-brand-charcoal dark:text-stone-300" data-id="element-3054">
                              Multi-load orders require separate deliveries. You
                              will be charged a delivery fee per load.
                            </p>
                          </div>
                        </motion.div>}
                    </AnimatePresence>
                  </div>

                  {/* Standalone "Get a Custom Quote" button removed —
                   the amber "Need 5+ loads? Get a custom quote →" link
                   inside the readout panel above is now the single,
                   unambiguous entry point to the quote modal. */}

                  {/* "Need a custom amount? Call us..." line removed —
                   customers can dial in any 3-yd-stepped amount via
                   the quantity dropdown and run the inline calculator
                   above for project-based sizing.
                   "Quiz & Calculator" pill also removed — the inline
                   calculator above and the in-context CartHelper cover
                   both of those jobs without bouncing the user away. */}
                  <div className="flex flex-wrap items-center justify-center gap-2 pt-3 mt-1 border-t border-brand-charcoal/10 dark:border-white/10" data-id="element-3055">
                    <Link to={`/compare?add=${encodeURIComponent(product.slug)}`} className="text-xs font-medium text-brand-charcoal/80 dark:text-white/80 bg-brand-charcoal/5 dark:bg-white/5 hover:bg-brand-orange/15 hover:text-brand-orange dark:hover:text-brand-orange border border-brand-charcoal/10 dark:border-white/10 hover:border-brand-orange/40 rounded-full px-3 py-1.5 transition-colors" data-id="element-3056">
                      Compare Materials
                    </Link>
                    <Link to="/diy-help" className="text-xs font-medium text-brand-charcoal/80 dark:text-white/80 bg-brand-charcoal/5 dark:bg-white/5 hover:bg-brand-orange/15 hover:text-brand-orange dark:hover:text-brand-orange border border-brand-charcoal/10 dark:border-white/10 hover:border-brand-orange/40 rounded-full px-3 py-1.5 transition-colors" data-id="element-3057">
                      DIY Help
                    </Link>
                  </div>
                </div>}
            </div>
          </div>
        </div>
      </div>

      <QuoteModal isOpen={isQuoteModalOpen} onClose={() => setIsQuoteModalOpen(false)} data-id="element-3058" />

      {/* Old fixed-position Calculator Drawer removed — replaced by the
                                                                                                                                                                                                    inline "Calculate how much I need" pulldown under the Add to Truck
                                                                                                                                                                                                    button, matching the Services hub product detail layout. */}
    </motion.main>;
}